/**
 * apiClient.js — Re Leve IA (Etapa 11)
 * Cliente opcional para a API backend Node.js + Fastify.
 *
 * REGRA DE OURO:
 *   - Só usa a API se VITE_ENABLE_REAL_API=true E VITE_API_BASE_URL estiver configurado.
 *   - Se a API falhar por qualquer motivo, retorna null — o chamador cai no motor local.
 *   - Nunca quebra o chat. Nunca mostra erros técnicos ao usuário.
 *   - Nunca envia segredos. Nenhuma chave no front-end.
 */

import appConfig from '../config/appConfig';

const API_BASE = appConfig.apiBaseUrl || '';
const API_ENABLED = appConfig.enableRealApi && API_BASE !== '';

const TIMEOUT_MS = 5000;

async function apiFetch(path, options = {}) {
  if (!API_ENABLED) return null;

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);

  try {
    const res = await fetch(`${API_BASE}${path}`, {
      headers: { 'Content-Type': 'application/json' },
      signal: controller.signal,
      ...options,
    });
    clearTimeout(timer);
    if (!res.ok) return null;
    return await res.json();
  } catch {
    clearTimeout(timer);
    return null;
  }
}

// ── Endpoints gerais ───────────────────────────────────────────────────────────

export async function apiHealth() {
  return apiFetch('/health');
}

// Status resumido só de LLM/harness/n8n/faq, derivado de /health
export async function apiLLMStatus() {
  const h = await apiHealth();
  if (!h) return null;
  return {
    llm: h.llm || null,
    n8n: h.n8n || null,
    harness: h.harness || null,
    faq: h.faq || null,
  };
}

// ── Chat / FAQ ─────────────────────────────────────────────────────────────────

export async function sendChatToApi(payload) {
  // payload pode incluir useCase ('sales_relationship' | 'faq')
  return apiFetch('/api/chat', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function sendFAQToApi(payload) {
  // alias: usa endpoint dedicado /api/faq
  return apiFetch('/api/faq', {
    method: 'POST',
    body: JSON.stringify({ ...payload, useCase: 'faq' }),
  });
}

export async function sendConsentToApi(payload) {
  return apiFetch('/api/consent', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function sendEventToApi(payload) {
  return apiFetch('/api/events', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

// Testar webhook n8n via Developer Lab (sem segredo)
export async function sendN8NTestEvent(payload) {
  return apiFetch('/api/events/test-n8n', {
    method: 'POST',
    body: JSON.stringify(payload || { eventType: 'test_event', source: 'developer_lab' }),
  });
}

export async function getKnowledgeFromApi(query) {
  const qs = query ? `?q=${encodeURIComponent(query)}` : '';
  return apiFetch(`/api/knowledge${qs}`);
}

export async function getGuardrailsFromApi() {
  return apiFetch('/api/guardrails');
}

export async function getWhatsAppSummaryFromApi(payload) {
  return apiFetch('/api/whatsapp/summary', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

// ── Pagamentos ─────────────────────────────────────────────────────────────────

export async function createPaymentCheckout(payload) {
  return apiFetch('/api/payments/create-checkout', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function getPaymentStatus(paymentId) {
  return apiFetch(`/api/payments/${paymentId}`);
}

export async function simulatePaymentSuccess(payload) {
  return apiFetch('/api/payments/simulate-success', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function getKitPricesFromApi() {
  return apiFetch('/api/payments/kit-prices');
}

export const isApiEnabled = API_ENABLED;
export const apiBaseUrl = API_BASE;
